// @flow
import update from 'immutability-helper';
import { ofType } from 'redux-observable';
import { mergeMap, map, catchError } from 'rxjs/operators';
import { endpoints, uploadImageApi, setItemInvoiceApi, setIdentityApi, progressPurchaseApi } from './api';
import { of } from 'rxjs';
import { setNewAlert } from '../../../alert/duck';

import type { Order } from '../../../../entities';
import type { HTTPError } from '../../../../error';
import type { Observable } from 'rxjs';
import type { UploadImageResp } from './api';

export type RegisterPurchaseAction = {
  +type: string;
  +imageData?: FormData;
  +ext?: string;
  +order?: Order;
  +orderId?: string;
  +itemNumber?: string;
  +identityData?: FormData;
  +error?: HTTPError;
};

export type RegisterPurchaseState = {
  itemLoadingMap: {
    [key: string]: boolean;
  };
  itemErrorMap: {
    [key: string]: HTTPError;
  };
  isPostingIdentity?: boolean;
  postIdentityError?: HTTPError;
  isProgressing?: boolean;
  progressError?: HTTPError;
};

const REGISTER_PURCHASE_UPLOAD_INVOICE_FILE_REQUEST = 'REGISTER_PURCHASE_UPLOAD_INVOICE_FILE_REQUEST';
const REGISTER_PURCHASE_UPLOAD_INVOICE_REQUEST = 'REGISTER_PURCHASE_UPLOAD_INVOICE_REQUEST';
const REGISTER_PURCHASE_UPLOAD_INVOICE_SUCCESS = 'REGISTER_PURCHASE_UPLOAD_INVOICE_SUCCESS';
const REGISTER_PURCHASE_UPLOAD_INVOICE_ERROR = 'REGISTER_PURCHASE_UPLOAD_INVOICE_ERROR';

const REGISTER_PURCHASE_POST_IDENTITY_REQUEST = 'REGISTER_PURCHASE_POST_IDENTITY_REQUEST';
const REGISTER_PURCHASE_POST_IDENTITY_SUCCESS = 'REGISTER_PURCHASE_POST_IDENTITY_SUCCESS';
const REGISTER_PURCHASE_POST_IDENTITY_ERROR = 'REGISTER_PURCHASE_POST_IDENTITY_ERROR';

const REGISTER_PURCHASE_PROGRESS_PURCHASE_REQUEST = 'REGISTER_PURCHASE_PROGRESS_PURCHASE_REQUEST';
const REGISTER_PURCHASE_PROGRESS_PURCHASE_SUCCESS = 'REGISTER_PURCHASE_PROGRESS_PURCHASE_SUCCESS';
const REGISTER_PURCHASE_PROGRESS_PURCHASE_ERROR = 'REGISTER_PURCHASE_PROGRESS_PURCHASE_ERROR';

// Action Creators
export function uploadInvoiceFile(orderId: string, itemNumber: string, imageData: FormData, ext: string): RegisterPurchaseAction {
  return {
    type: REGISTER_PURCHASE_UPLOAD_INVOICE_FILE_REQUEST,
    orderId,
    itemNumber,
    imageData,
    ext,
  };
}

export function uploadInvoice(orderId: string, itemNumber: string, imageId: string, ext: string): RegisterPurchaseAction {
  return {
    type: REGISTER_PURCHASE_UPLOAD_INVOICE_REQUEST,
    orderId,
    itemNumber,
    imageId,
    ext,
  };
}

export function uploadInvoiceSuccess(itemNumber: string, order: Order): RegisterPurchaseAction {
  return {
    type: REGISTER_PURCHASE_UPLOAD_INVOICE_SUCCESS,
    itemNumber,
    order,
  };
}

export function uploadInvoiceError(error: HTTPError, itemNumber: string): RegisterPurchaseAction {
  return {
    type: REGISTER_PURCHASE_UPLOAD_INVOICE_ERROR,
    error,
    itemNumber,
  };
}

export function postIdentity(orderId: string, identityData: FormData): RegisterPurchaseAction {
  return {
    type: REGISTER_PURCHASE_POST_IDENTITY_REQUEST,
    orderId,
    identityData,
  };
}

export function postIdentitySuccess(order: Order): RegisterPurchaseAction {
  return {
    type: REGISTER_PURCHASE_POST_IDENTITY_SUCCESS,
    order,
  };
}

export function postIdentityErrorFn(error: HTTPError): RegisterPurchaseAction {
  return {
    type: REGISTER_PURCHASE_POST_IDENTITY_ERROR,
    error,
  };
}

export function progressPurchase(orderId: string): RegisterPurchaseAction {
  return {
    type: REGISTER_PURCHASE_PROGRESS_PURCHASE_REQUEST,
    orderId,
  };
}

export function progressPurchaseSuccess(order: Order): RegisterPurchaseAction {
  return {
    type: REGISTER_PURCHASE_PROGRESS_PURCHASE_SUCCESS,
    order,
  };
}

export function progressPurchaseError(error: HTTPError): RegisterPurchaseAction {
  return {
    type: REGISTER_PURCHASE_PROGRESS_PURCHASE_ERROR,
    error,
  };
}

export const actions = {
  REGISTER_PURCHASE_UPLOAD_INVOICE_FILE_REQUEST,
  REGISTER_PURCHASE_UPLOAD_INVOICE_REQUEST,
  REGISTER_PURCHASE_UPLOAD_INVOICE_SUCCESS,
  REGISTER_PURCHASE_UPLOAD_INVOICE_ERROR,
  REGISTER_PURCHASE_POST_IDENTITY_REQUEST,
  REGISTER_PURCHASE_POST_IDENTITY_SUCCESS,
  REGISTER_PURCHASE_POST_IDENTITY_ERROR,
  REGISTER_PURCHASE_PROGRESS_PURCHASE_REQUEST,
  REGISTER_PURCHASE_PROGRESS_PURCHASE_SUCCESS,
  REGISTER_PURCHASE_PROGRESS_PURCHASE_ERROR,
  uploadInvoiceFile,
  uploadInvoice,
  uploadInvoiceSuccess,
  uploadInvoiceError,
  postIdentity,
  postIdentitySuccess,
  postIdentityErrorFn,
  progressPurchase,
  progressPurchaseSuccess,
  progressPurchaseError,
};


export const initialState: RegisterPurchaseState = {
  itemLoadingMap: {},
  itemErrorMap: {},
};

export default function reducer(state: RegisterPurchaseState = initialState,
                                action: RegisterPurchaseAction): RegisterPurchaseState {
  switch (action.type) {
    case REGISTER_PURCHASE_UPLOAD_INVOICE_FILE_REQUEST:
      if (typeof action.itemNumber !== 'number') {
        return state;
      }
      return update(state, {
        itemLoadingMap: {
          [`${action.itemNumber}`]: { $set: true },
        },
        itemErrorMap: {
          $unset: [`${action.itemNumber}`],
        },
      });
    case REGISTER_PURCHASE_UPLOAD_INVOICE_SUCCESS:
      if (typeof action.itemNumber !== 'number') {
        return state;
      }
      return update(state, {
        itemLoadingMap: {
          [`${action.itemNumber}`]: { $set: false },
        },
        itemErrorMap: {
          $unset: [`${action.itemNumber}`],
        },
      });
    case REGISTER_PURCHASE_UPLOAD_INVOICE_ERROR:
      if (typeof action.itemNumber !== 'number') {
        return state;
      }
      return update(state, {
        itemLoadingMap: {
          [`${action.itemNumber}`]: { $set: false },
        },
        itemErrorMap: {
          [`${action.itemNumber}`]: { $set: action.error },
        },
      });
    case REGISTER_PURCHASE_POST_IDENTITY_REQUEST:
      return update(state, {
        isPostingIdentity: { $set: true },
        $unset: ['postIdentityError'],
      });
    case REGISTER_PURCHASE_POST_IDENTITY_SUCCESS:
      return update(state, {
        $unset: ['isPostingIdentity'],
      });
    case REGISTER_PURCHASE_POST_IDENTITY_ERROR:
      return update(state, {
        postIdentityError: { $set: action.error },  
        $unset: ['isPostingIdentity'],
      });
    case REGISTER_PURCHASE_PROGRESS_PURCHASE_REQUEST:
      return update(state, {
        isProgressing: { $set: true },
        $unset: ['progressError'],
      });
    case REGISTER_PURCHASE_PROGRESS_PURCHASE_SUCCESS:
      return update(state, {
        $unset: ['isProgressing'],
      });
    case REGISTER_PURCHASE_PROGRESS_PURCHASE_ERROR:
      return update(state, {
        progressError: { $set: action.error },
        $unset: ['isProgressing'],
      });
    default:
      return state;
  }
}

//Epics
export function registerPurchaseUploadInvoiceFileEpic(action$: Observable<RegisterPurchaseAction>): Observable<RegisterPurchaseAction> {
  return action$.pipe(
    ofType(REGISTER_PURCHASE_UPLOAD_INVOICE_FILE_REQUEST),
    mergeMap(({ orderId, itemNumber, imageData, ext }) =>
      uploadImageApi(imageData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }).pipe(
        map((uploadImageResp: UploadImageResp) => uploadInvoice(orderId, itemNumber, uploadImageResp.id, ext)),
        catchError((e: HTTPError) => of(uploadInvoiceError(e, itemNumber))))
    ));
}

export function registerPurchaseUploadInvoiceEpic(action$: Observable<RegisterPurchaseAction>): Observable<RegisterPurchaseAction> {
  return action$.pipe(
    ofType(REGISTER_PURCHASE_UPLOAD_INVOICE_REQUEST),
    mergeMap(({ orderId, itemNumber, imageId, ext }) =>
      setItemInvoiceApi({ storedFileId: imageId, fileExtension: ext }, {
        route: endpoints.SET_ITEM_INVOICE
          .replace(':orderId', orderId)
          .replace(':itemNumber', itemNumber),
      }).pipe(
        map((order: Order) => {
          return uploadInvoiceSuccess(itemNumber, order);
        }),
        catchError((e: HTTPError) => of(uploadInvoiceError(e, itemNumber))))
    ));
}

export function registerPurchasePostIdentityEpic(action$: Observable<RegisterPurchaseAction>): Observable<RegisterPurchaseAction> {
  return action$.pipe(
    ofType(REGISTER_PURCHASE_POST_IDENTITY_REQUEST),
    mergeMap(({ orderId, identityData }) =>
      setIdentityApi(identityData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        route: endpoints.SET_IDENTITY
          .replace(':orderId', orderId),
      }).pipe(
        mergeMap((order: Order) => of(postIdentitySuccess(order), progressPurchase(orderId))),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(postIdentityErrorFn(e),
              setNewAlert('order', 'error', undefined, 'httpErrors.post.500'),
            );
          }
          if (Math.round(e.code / 100) === 4) {
            return of(postIdentityErrorFn(e),
              setNewAlert('order', 'error', undefined, 'httpErrors.post.400'),
            );
          }
          return of(postIdentityErrorFn(e));
        }))
    ));
}

export function registerPurchaseProgressPurchaseEpic(action$: Observable<RegisterPurchaseAction>): Observable<RegisterPurchaseAction> {
  return action$.pipe(
    ofType(REGISTER_PURCHASE_PROGRESS_PURCHASE_REQUEST),
    mergeMap(({ orderId }) => progressPurchaseApi(undefined, {
        route: endpoints.PROGRESS_PURCHASE.replace(':orderId', orderId),
      }).pipe(
      map(progressPurchaseSuccess),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(progressPurchaseError(e),
            setNewAlert('order', 'error', undefined, 'httpErrors.post.500'),
          );
        }
        return of(progressPurchaseError(e))
      }))
    ));
}



// WEBPACK FOOTER //
// ./src/modules/order/Purchase/register-purchase/duck.js